<script setup lang="ts">
    import { UnwrapNestedRefs } from 'nuxt/dist/app/compat/capi';
    import ImportedFileEndpoint from '../../../app/common/endpoints/ImportedFileEndpoint';
    import ImportedFile from '../../../app/common/models/ImportedFile';
    import { Importable } from '../../../app/common/enums/Importable';
    import Contact from '../../../app/contact/models/Contact';
    import { CONTACT_TYPE } from '../../../app/contact/enums/ContactType';
    import ImportUpload from './ImportUpload.vue';
    import ImportSample from './ImportSample.vue';
    import Product from '@/app/inventory/models/Product';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import { useEvent } from '@/composables/useEventBus';
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import { ContactImportErrors, isCsvFile, parseContactImportErrors } from '~~/app/contact/import/contact-import';

    const loading = ref(true);
    export interface Fields {
        isSupplier: boolean;
    }
    const form = useForm<Fields>({
        isSupplier: false,
    });

    const { modalName, model } = useModal('contact:contact:import-modal', (payload?: UnwrapNestedRefs<Product>) => onOpened(payload));

    const contactQueryType = ref();
    const router = useRouter();
    const route = useRoute();
    const getUrlQueryParams = async () => {
        // router is async so we wait for it to be ready
        await router.isReady();
        // once its ready we can access the query params
        contactQueryType.value = route.query.tab || 'client';
    };
    const onOpened = async (payload?: UnwrapNestedRefs<Contact>) => {
        await getUrlQueryParams();
        resetErros();
        form.reset();
        form.set('isSupplier', contactQueryType.value !== 'client');
        loading.value = true;
        file.value = undefined;
        shouldDisplay.value = true;
        isUploadable.value = false;
        inputLabel.value = t('actions.upload');
        loading.value = false;
    };

    const shouldDisplay = ref<boolean>(true);

    const toggle = () => {
        shouldDisplay.value = !shouldDisplay.value;
    };

    const { t } = useTranslation();
    const { trackValidationStarted, trackValidationFailed, trackValidationSucceeded } = useContactImportTracking();
    const file = ref<File>();
    const inputLabel = ref<string>(t('actions.upload'));
    const fileChanged = ($event: Event) => {
        const target = $event.target as HTMLInputElement;
        const selected = target?.files?.[0];
        if (!selected) return;
        resetErros();
        if (!isCsvFile(selected)) {
            target.value = '';
            file.value = undefined;
            inputLabel.value = t('actions.upload');
            importErrors.value = { kind: 'invalid_file_type', details: [] };
            return;
        }
        file.value = selected;
        inputLabel.value = selected.name;
    };

    const isUploadable = ref<boolean>(false);
    const isRequesting = ref<boolean>(false);
    const importedFile = ref<ImportedFile | null>();
    const importErrors = ref<ContactImportErrors | null>(null);

    const resetErros = () => {
        importErrors.value = null;
    };
    const validate = async () => {
        if (!file.value) return;
        resetErros();
        isRequesting.value = true;
        isUploadable.value = false;
        const endpoint: ImportedFileEndpoint = EndpointFactory.make(ModelType.IMPORTED_FILE) as ImportedFileEndpoint;
        const contactType = form.get('isSupplier') ? CONTACT_TYPE.SUPPLIER : CONTACT_TYPE.CLIENT;
        trackValidationStarted(contactType);
        const response = await endpoint.validated(file.value, 'clients', contactType);
        isRequesting.value = false;
        if (response.error) {
            importErrors.value = parseContactImportErrors(response.error);
            trackValidationFailed(contactType, importErrors.value.kind, importErrors.value.details.length);
            return;
        }
        importedFile.value = response.data;
        isUploadable.value = true;
        trackValidationSucceeded(contactType);

        useToasteoSuccess();
    };

    const isUploading = ref<boolean>(false);
    const upload = async () => {
        isUploading.value = true;
        const endpoint: ImportedFileEndpoint = EndpointFactory.make(ModelType.IMPORTED_FILE) as ImportedFileEndpoint;
        const contactType = form.get('isSupplier') ? CONTACT_TYPE.SUPPLIER : CONTACT_TYPE.CLIENT;
        const response = await endpoint.upload(importedFile.value?.getId() as string, Importable.CLIENT, contactType);

        if (response.error) {
            if (!response.validationErrors) {
                useToasteoError();
                isUploading.value = false;
            }
            return;
        }
        isUploading.value = false;
        useToasteoSuccess();
        close();
        return navigateTo({ name: 'invoicing-import' });
    };

    const close = () => {
        // form.reset();
        resetErros();

        useEvent(`${modalName}:close`);
    };
</script>

<template>
    <Modal
        ref="importModal"
        name="contact:contact:import-modal"
        :width="1100"
    >
        <ModalLoader v-if="loading"></ModalLoader>
        <template v-else>
            <div :class="$theme('modal.title.container')">
                <h2
                    :class="$theme('modal.title.text')"
                    class="pt-4 text-center first-letter:capitalize whitespace-nowrap"
                >
                    {{ form.get('isSupplier') ? $t('contact.supplier.import_form.title') : $t('contact.contact.import_form.title') }}
                </h2>
            </div>
            <div
                :class="$theme('modal.padding')"
                class="space-y-4 text-center"
            >
                <ImportSample
                    v-if="shouldDisplay"
                    @close="close()"
                    @continue="toggle()"
                />
                <ImportUpload
                    v-else-if="!shouldDisplay && !isUploadable"
                    :file="file"
                    :label="inputLabel"
                    :loading="isRequesting"
                    :form="form"
                    @previous="toggle()"
                    @validate="validate()"
                    @file-changed="($event) => fileChanged($event)"
                >
                    <ContactContactImportErrors
                        v-if="importErrors"
                        :errors="importErrors"
                    />
                </ImportUpload>
                <ContactContactImportPreview
                    v-if="isUploadable"
                    :loading="isUploading"
                    :have-file-selected="!file"
                    :imported-file="importedFile"
                    @previous="isUploadable = !isUploadable"
                    @upload="upload()"
                />
            </div>
        </template>
    </Modal>
</template>
